import { useCallback, useEffect, useState } from "react";

export const SIDEBAR_COLLAPSED_KEY = "phazex.sidebar.collapsed";
export const SIDEBAR_EXPANDED_SECTIONS_KEY = "phazex.sidebar.expandedSections";

function readCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (raw === null) return false;
    const parsed = JSON.parse(raw);
    if (typeof parsed === "boolean") return parsed;
  } catch {
    // ignore
  }
  return false;
}

function readExpandedSections(): Record<string, boolean> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(SIDEBAR_EXPANDED_SECTIONS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const out: Record<string, boolean> = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (typeof v === "boolean") out[k] = v;
      }
      return out;
    }
  } catch {
    // ignore
  }
  return {};
}

type Updater<T> = T | ((prev: T) => T);

export function useSidebarCollapsed(): [
  boolean,
  (next: Updater<boolean>) => void,
] {
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed());

  useEffect(() => {
    try {
      window.localStorage.setItem(
        SIDEBAR_COLLAPSED_KEY,
        JSON.stringify(collapsed),
      );
    } catch {
      // ignore
    }
  }, [collapsed]);

  const setValue = useCallback((next: Updater<boolean>) => {
    setCollapsed((prev) =>
      typeof next === "function" ? (next as (p: boolean) => boolean)(prev) : next,
    );
  }, []);

  return [collapsed, setValue];
}

export function useSidebarExpandedSections(): [
  Record<string, boolean>,
  (next: Updater<Record<string, boolean>>) => void,
] {
  const [sections, setSections] = useState<Record<string, boolean>>(() =>
    readExpandedSections(),
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(
        SIDEBAR_EXPANDED_SECTIONS_KEY,
        JSON.stringify(sections),
      );
    } catch {
      // ignore
    }
  }, [sections]);

  const setValue = useCallback(
    (next: Updater<Record<string, boolean>>) => {
      setSections((prev) =>
        typeof next === "function"
          ? (next as (p: Record<string, boolean>) => Record<string, boolean>)(
              prev,
            )
          : next,
      );
    },
    [],
  );

  return [sections, setValue];
}
